<template>
    <div class="DispatchChannelToggles">
        <div
            v-for="channel in channels"
            :key="channel.key"
            class="row"
            :class="{ isDisabled: !!unavailableReason(channel.key) }"
        >
            <FormToggle
                :value="isOn(channel.key)"
                :disabled="!!unavailableReason(channel.key)"
                @input="setChannel(channel.key, $event)"
            />
            <div class="label">
                <h4>{{ channel.label }}</h4>
                <small v-if="unavailableReason(channel.key)">
                    {{ unavailableReason(channel.key) }}
                </small>
                <small v-else-if="channel.note">{{ channel.note }}</small>
            </div>
        </div>

        <hr/>

        <div class="row">
            <FormToggle :value="isOn('manual')" @input="setChannel('manual', $event)"/>
            <div class="label">
                <h4>Don't notify automatically</h4>
                <small>Record the dispatch as sent without messaging the driver.</small>
            </div>
        </div>
    </div>
</template>

<script>
import FormToggle from '@/components/common/FormToggle.vue';

export default {
    name: 'DispatchChannelToggles',

    components: { FormToggle },

    props: {
        value: { type: Array, default: null },
        emailAvailable: { type: Boolean, default: true },
        phoneAvailable: { type: Boolean, default: true },
    },

    data: () => ({
        // The notes cover what having an address on file does not: push needs a
        // registered device and SMS needs consent, both of which live in the dispatch
        // service and cannot be checked from here. Wording follows its send modal so
        // the two do not tell a manager different things.
        channels: [
            {
                key: 'app',
                label: 'Mobile app',
                note: 'Needs the DumpTek app installed and notifications allowed.',
            },
            {
                key: 'sms',
                label: 'Text message',
                note: 'Needs the driver to have agreed to be texted.',
            },
            { key: 'email', label: 'Email' },
        ],
        // Set synchronously on every change, so a second change in the same tick builds
        // on the first. The `value` prop only catches up once the parent has round-tripped
        // the emit, and computing from it alone meant two rapid changes collapsed to
        // whichever landed last -- the display and the saved value could disagree.
        pending: null,
    }),

    computed: {
        selected() {
            if (this.pending !== null) return this.pending;

            return this.value || [];
        },
    },

    watch: {
        // The parent has caught up, so go back to tracking the prop. Also covers a
        // parent that rewrites or rejects what we emitted.
        value() {
            this.pending = null;
        },
    },

    methods: {
        isOn(channel) {
            return this.selected.includes(channel);
        },

        unavailableReason(channel) {
            if (channel === 'sms' && !this.phoneAvailable) {
                return 'No phone number on file for this driver.';
            }

            if (channel === 'email' && !this.emailAvailable) {
                return 'No email address on file for this driver.';
            }

            return null;
        },

        // Always emits a brand new array. Both hosts bind this through a computed setter
        // (v-model, or the syncProps mixin), and mutating the existing array in place
        // never invokes that setter, so the change would be silently dropped.
        setChannel(channel, enabled) {
            let next;

            if (!enabled) {
                next = this.selected.filter(value => value !== channel);
            } else if (channel === 'manual') {
                // Manual means "notify nobody", so it cannot coexist with a real channel.
                next = ['manual'];
            } else {
                next = [...this.selected.filter(value => value !== 'manual'), channel];
            }

            // Keep a stable order so a set never depends on the order it was clicked in.
            const order = ['app', 'sms', 'email', 'manual'];
            const ordered = order.filter(value => next.includes(value));

            this.pending = ordered;
            this.$emit('input', ordered);
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'globals.scss';
.DispatchChannelToggles {
    .row {
        display: flex;
        align-items: center;
        margin-bottom: 15px;

        h4 {
            margin: 0;
        }

        .FormToggle {
            margin-right: 15px;
            flex-shrink: 0;
        }

        small {
            display: block;
            color: $text-color-muted;
        }

        &.isDisabled {
            .FormToggle {
                opacity: 0.5;
            }

            h4 {
                color: $text-color-muted;
            }
        }
    }

    hr {
        border: none;
        border-top: 1px solid $divider-color;
        margin: 20px 0 15px;
    }
}
</style>
